import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Home, Sun, Mountain, Fish, Wifi, Clock, LeafyGreen, BrickWallIcon, FootprintsIcon, SpadeIcon, ShowerHeadIcon, ScissorsIcon, BedDoubleIcon, BedDouble, PcCaseIcon, FlameIcon, CandyIcon, HammerIcon, CoinsIcon, LeafIcon, GlassWaterIcon, ChurchIcon, TvIcon } from 'lucide-react';
import ReactPlayer from 'react-player'

const PropertyLandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '' });
  const heroSectionRef = useRef(null);
  const nextSectionRef = useRef(null);

  const features = [
    { icon: Mountain, text: "Breathtaking mountain & bush surroundings in Mpumalanga" },
    { icon: Fish, text: "Waterfall, fishing river, sparkling pools & clubhouse" },
    { icon: FootprintsIcon, text: "Hiking & Mountain biking trails" },
    { icon: BrickWallIcon, text: "Tennis court, Mashie Golf Course" }, 
    { icon: Wifi, text: "Perfect for retirement, holiday home, or remote work setup" },
    { icon: CoinsIcon, text: "Affordable levies - great value living" },
    { icon: Clock, text: "24/7 Remote-controlled gate access & security" },
    { icon: GlassWaterIcon, text: "Natural water source - self-sufficient living" },   
    { icon: LeafIcon, text: "Natural open hillsides and biodiversity planting" },   
    { icon: ChurchIcon, text: "Sunday community gatherings" },   
    { icon: SpadeIcon, text: "Beautifully maintained common areas" }
  ];

  const featuresImages = [
    { src: "/images/features/clubhouse_big.jpg", alt: "Hephzibah Estate Clubhouse Mpumalanga", caption: "Community Clubhouse" },
    { src: "/images/features/gardens.jpg", alt: "Hephzibah Estate Gardens", caption: "Fully Maintained Estate Gardens" },
    { src: "/images/features/hiking.jpg", alt: "Hiking Trails Mpumalanga", caption: "Multiple Hiking Trails" },
    { src: "/images/features/fishing.jpg", alt: "Fishing at Hephzibah Estate", caption: "Community Catch & Release Dams" },
    { src: "/images/features/nature.jpg", alt: "Eco-friendly living Mpumalanga", caption: "Eco friendly living" },
    { src: "/images/features/nature1.jpg", alt: "Biodiversity Hephzibah", caption: "Surrounded by bio-diversity" },
    { src: "/images/features/pool1.jpg", alt: "Swimming Pools Hephzibah Estate", caption: "2 Well Maintained Swimming Pools" },
    { src: "/images/features/pools.jpg", alt: "Community pools Mpumalanga", caption: "2 Well Maintained Swimming Pools" },
    { src: "/images/features/sunset.jpg", alt: "Mpumalanga sunset views", caption: "Amazing Sunset Views" },
    { src: "/images/features/sunset3.jpg", alt: "Beautiful sunsets Hephzibah", caption: "Beautiful Sunsets" },
    { src: "/images/features/tennis.jpg", alt: "Tennis Court Hephzibah Estate", caption: "Tennis Court and Mini Fun Mashie Course" },
    { src: "/images/features/vista.jpg", alt: "Panoramic Views Mpumalanga", caption: "Amazing Panoramic Views" },
    { src: "/images/features/water.jpg", alt: "Natural Water Sources Hephzibah", caption: "Self Reliance: Amazing Natural Water Sources" },
    { src: "/images/features/waterfall.jpg", alt: "Waterfall Hephzibah Estate", caption: "Amazing Natural Waterfall" },
  ];

  const handleExploreClick = () => {
    if (nextSectionRef.current) {
      nextSectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const houseImages = [
    { src: "/images/house1.jpg", alt: "3-bedroom home for sale Hephzibah Estate Mpumalanga", caption: "Well Planned North Facing Home with Large Patio" },
    { src: "/images/house2.jpg", alt: "House with double carport Mpumalanga", caption: "Double Car Port" },
    { src: "/images/inside1.jpg", alt: "Open plan living Hephzibah home", caption: "Spacious Open-Plan Living" },
    { src: "/images/inside3.jpg", alt: "Master bedroom Mpumalanga property", caption: "Cozy Master Bedroom" },
    { src: "/images/kitchen1.jpg", alt: "Kitchen Hephzibah Estate home", caption: "Cosy, Ergonomically Designed Kitchen" },
    { src: "/images/mainbed1.jpg", alt: "Large main bedroom Mpumalanga", caption: "Spacious Main Bedroom with En-Suite Bathroom & Loads of Cupboard Space" },
    { src: "/images/onsuite-bath.jpg", alt: "En-suite bathroom Hephzibah", caption: "En-Suite Shower-Based Bathroom" },
    { src: "/images/room2.jpg", alt: "Second bedroom craft room Mpumalanga", caption: "Well-Lit Enormous Craft, Hobby or 2nd Bedroom" },
    { src: "/images/scullery1.jpg", alt: "Scullery kitchen Hephzibah home", caption: "Spacious Scullery off Kitchen" },
    { src: "/images/study.jpg", alt: "Home office space Mpumalanga", caption: "Well-Lit Spacious Passage Way Office Area" }
  ];

  const [currentFeatureImageIndex, setCurrentFeatureImageIndex] = useState(0);
  const nextFeatureImage = () => setCurrentFeatureImageIndex((prev) => (prev + 1) % featuresImages.length);
  const prevFeatureImage = () => setCurrentFeatureImageIndex((prev) => (prev - 1 + featuresImages.length) % featuresImages.length);

  const [currentHouseImageIndex, setCurrentHouseImageIndex] = useState(0);
  const nextHouseImage = () => setCurrentHouseImageIndex((prev) => (prev + 1) % houseImages.length);
  const prevHouseImage = () => setCurrentHouseImageIndex((prev) => (prev - 1 + houseImages.length) % houseImages.length);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you! An agent will contact you soon.');
    setContactForm({ name: '', phone: '', email: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* FIXED: Added Header Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-green-800 bg-opacity-95 backdrop-blur-sm shadow-lg">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Home className="text-white" size={24} />
              <h2 className="text-white font-bold text-lg">Hephzibah Estate</h2>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+27826535566" className="text-white hover:text-green-300 transition text-sm md:text-base">
                📞 082 653 5566
              </a>
              <a 
                href="https://wa.me/27826535566" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* FIXED: Added padding-top to account for fixed header */}
      <div className="pt-16">
        {/* Hero Section */}
        <div 
          className="flex flex-col relative min-h-screen bg-center bg-no-repeat justify-center items-center py-12" 
          style={{ 
            backgroundColor: '#49764BB3', 
            backgroundImage: 'url("/images/features/sunset2.jpg")',
            backgroundSize: 'contain', 
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black opacity-50"></div>
          
          <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-[90%] lg:max-w-[80%]">
            {/* TV Feature Badge */}
            <div className="bg-red-600 text-white px-6 py-2 rounded-full mb-4 flex items-center gap-2 animate-pulse">
              <TvIcon size={20} />
              <span className="font-bold">AS FEATURED ON TV</span>
            </div>

            <div className="bg-orange-400 bg-opacity-90 p-8 rounded-lg text-center max-w-[700px] w-full shadow-2xl">
              {/* Main H1 - Critical for SEO */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Hephzibah Estate: 3-Bedroom Nature Home for Sale | Mpumalanga, South Africa
              </h1>
              
              <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
                <p className="text-white text-lg md:text-xl mb-3">
                  <span className="font-bold">Featured on TV!</span> Discover your dream home in Mpumalanga's premier Hephzibah nature estate. This stunning 3-bedroom property sits in a private valley offering breathtaking mountain views and peaceful living.
                </p>
                <p className="text-white text-md md:text-lg">
                  Perfect for <span className="font-semibold">retirees seeking tranquility</span>, families wanting a <span className="font-semibold">holiday escape</span>, or <span className="font-semibold">remote professionals</span> craving work-life balance.
                </p>
              </div>

              {/* Property Highlights Badges */}
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">📍 Mpumalanga</span>
                <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">🏡 3 Bedrooms</span>
                <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">🌿 Nature Estate</span>
                <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">🔒 24/7 Security</span>
                <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold">☀️ Solar Powered</span>
              </div>

              <p className="text-white text-lg font-bold mb-2">R1,000,000</p>
              <p className="text-white text-sm">60 Minutes from Loskop Day & Panorama Route</p>
            </div>

            {/* Scroll Down Button */}
            <button 
              onClick={handleExploreClick}
              className="mt-8 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-full transition duration-300 shadow-lg flex items-center gap-2"
            >
              Explore Property Details
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* FIXED: Video Section - Now properly separated from hero */}
        <div ref={nextSectionRef} className="container mx-auto px-4 py-12 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-6 text-gray-800">
              Take a Virtual Tour
            </h2>
            <div className="bg-gray-100 rounded-lg shadow-lg p-6">
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  src='/images/features/heph.mp4'
                  controls
                  className="w-full h-full"
                  poster="/images/features/sunset2.jpg"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="text-center text-gray-600 mt-4 text-sm">
                Experience the beauty of Hephzibah Estate through this stunning video tour
              </p>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="container mx-auto px-4 py-16 bg-gradient-to-b from-white to-green-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-6 text-gray-800">
              About Hephzibah Estate, Mpumalanga
            </h2>
            
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                <span className="font-bold text-green-700">Hephzibah Estate</span> is Mpumalanga's hidden gem, offering the perfect blend of <span className="font-semibold">tranquil countryside living</span> and modern conveniences. Recently <span className="font-semibold">featured on television</span>, this exclusive community has captured the hearts of South Africans seeking an escape from city life.
              </p>
              
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Located in the heart of <span className="font-semibold">Mpumalanga province</span>, Hephzibah Estate boasts stunning natural beauty with rolling hills, indigenous vegetation, and an abundance of wildlife. The estate is perfectly positioned for those who want to be close to nature while maintaining easy access to urban amenities.
              </p>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Just a short drive from the world-famous <span className="font-semibold text-green-700">Loskop Dam</span> and along the spectacular <span className="font-semibold text-green-700">Panorama Route</span>, residents enjoy unparalleled access to some of South Africa's most breathtaking natural attractions. Whether you're looking for a <span className="font-semibold">retirement haven</span>, a <span className="font-semibold">weekend escape</span>, or a <span className="font-semibold">remote work paradise</span>, Hephzibah Estate offers the perfect setting for your dream lifestyle.
              </p>
            </div>

            {/* Location Highlights */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-4xl mb-3">🦁</div>
                <h3 className="font-bold text-gray-800 mb-2">60 min to Loskop Dam or Bronkhorstspruit Dam</h3>
                <p className="text-sm text-gray-600">Wildlife safaris at your doorstep</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-4xl mb-3">🏔️</div>
                <h3 className="font-bold text-gray-800 mb-2">Panorama Route</h3>
                <p className="text-sm text-gray-600">Scenic waterfalls & viewpoints</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-4xl mb-3">🚗</div>
                <h3 className="font-bold text-gray-800 mb-2">2.5 hrs from Joburg</h3>
                <p className="text-sm text-gray-600">Easy weekend getaway</p>
              </div>
            </div>
          </div>
        </div>

        {/* Hephzibah Estate Amenities Section */}
        <div className="container mx-auto px-4 py-16 bg-white">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Hephzibah Estate Amenities
          </h2>
          <p className="text-center text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
            Enjoy world-class facilities and amenities in this exclusive Mpumalanga nature estate
          </p>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Amenities List */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4 bg-green-50 p-4 rounded-lg hover:bg-green-100 transition">
                  <div className="flex-shrink-0 bg-green-600 text-white p-2 rounded-lg">
                    <feature.icon size={24} />
                  </div>
                  <p className="text-gray-700 font-medium pt-1">{feature.text}</p>
                </div>
              ))}
            </div>

            {/* FIXED: Image Gallery with proper sizing */}
            <div className="relative bg-gray-100 rounded-lg overflow-hidden shadow-lg">
              <div className="relative h-[500px]">
                <img
                  src={featuresImages[currentFeatureImageIndex].src}
                  alt={featuresImages[currentFeatureImageIndex].alt}
                  className="w-full h-full object-contain bg-gray-900"
                />
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevFeatureImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} className="text-gray-800" />
                </button>
                <button
                  onClick={nextFeatureImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition"
                  aria-label="Next image"
                >
                  <ChevronRight size={24} className="text-gray-800" />
                </button>

                {/* Image Caption */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                  <p className="text-white text-center font-semibold">
                    {featuresImages[currentFeatureImageIndex].caption}
                  </p>
                </div>
              </div>

              {/* Pagination Dots */}
              <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-2">
                {featuresImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentFeatureImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition ${
                      index === currentFeatureImageIndex ? 'bg-white w-6' : 'bg-white bg-opacity-50'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Property Details Section */}
        <div className="container mx-auto px-4 py-16 bg-gradient-to-b from-green-50 to-white">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Your Future Home Awaits
          </h2>
          <p className="text-center text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
            This beautifully designed 3-bedroom property offers everything you need for comfortable living in Mpumalanga's premier nature estate
          </p>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* FIXED: House Image Gallery with proper sizing */}
            <div className="relative bg-gray-100 rounded-lg overflow-hidden shadow-lg order-2 lg:order-1">
              <div className="relative h-[500px]">
                <img
                  src={houseImages[currentHouseImageIndex].src}
                  alt={houseImages[currentHouseImageIndex].alt}
                  className="w-full h-full object-contain bg-gray-900"
                />
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevHouseImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} className="text-gray-800" />
                </button>
                <button
                  onClick={nextHouseImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition"
                  aria-label="Next image"
                >
                  <ChevronRight size={24} className="text-gray-800" />
                </button>

                {/* Image Caption */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                  <p className="text-white text-center font-semibold">
                    {houseImages[currentHouseImageIndex].caption}
                  </p>
                </div>
              </div>

              {/* Pagination Dots */}
              <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-2">
                {houseImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentHouseImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition ${
                      index === currentHouseImageIndex ? 'bg-white w-6' : 'bg-white bg-opacity-50'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Property Features */}
            <div className="space-y-6 order-1 lg:order-2">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Property Highlights</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <BedDoubleIcon className="text-green-600" size={24} />
                    <span className="text-gray-700"><span className="font-semibold">3 Bedrooms</span> - spacious and well-lit</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ShowerHeadIcon className="text-green-600" size={24} />
                    <span className="text-gray-700"><span className="font-semibold">2 Bathrooms</span> - 1 onsuite</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Sun className="text-green-600" size={24} />
                    <span className="text-gray-700"><span className="font-semibold">Solar Power</span> - energy independence</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Home className="text-green-600" size={24} />
                    <span className="text-gray-700"><span className="font-semibold">2000m² Stand</span> - plenty of space</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CoinsIcon className="text-green-600" size={24} />
                    <span className="text-gray-700"><span className="font-semibold">Income Potential</span> - rental outbuildings</span>
                  </div>
                </div>
              </div>

              <div className="bg-green-600 text-white rounded-lg shadow-lg p-6">
                <h3 className="text-2xl font-bold mb-2">Price: R1,000,000</h3>
                <p className="text-green-100 mb-4">Exceptional value in Mpumalanga's premier nature estate</p>
                <a
                  href="https://wa.me/27826535566?text=Hi!%20I'm%20interested%20in%20the%203-bedroom%20home%20at%20Hephzibah%20Estate.%20Can%20I%20get%20more%20details?"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-white text-green-600 hover:bg-green-50 font-bold py-3 px-6 rounded-lg transition text-center"
                >
                  Get More Details
                </a>
              </div>

              <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-6">
                <h3 className="text-xl font-bold text-orange-800 mb-3">🏡 Perfect For:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><span className="font-semibold">Retirees</span> seeking peaceful living</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><span className="font-semibold">Remote workers</span> wanting work-life balance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><span className="font-semibold">Holiday home</span> investment near tourist attractions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 font-bold">•</span>
                    <span><span className="font-semibold">Families</span> seeking safe, nature-rich environment</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Property Details */}
        <div className="container mx-auto px-4 py-16 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Complete Property Features</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Interior Features */}
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-green-800 mb-4">Interior Features</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Open-plan living and dining area</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Large kitchen with scullery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Master bedroom with en-suite</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Built-in wardrobes throughout</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Home office/study area</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Large windows with mountain views</span>
                  </li>
                </ul>
              </div>

              {/* Exterior Features */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-blue-800 mb-4">Exterior Features</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">✓</span>
                    <span>2000m² landscaped garden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">✓</span>
                    <span>North-facing covered patio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">✓</span>
                    <span>Double carport parking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">✓</span>
                    <span>Solar power system installed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">✓</span>
                    <span>Rental cottage for extra income</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600">✓</span>
                    <span>Water storage tanks</span>
                  </li>
                </ul>
              </div>

              {/* Estate Security */}
              <div className="bg-yellow-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-yellow-800 mb-4">Security & Access</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>24/7 controlled access gate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Remote gate control system</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Security patrols</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Perimeter fencing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600">✓</span>
                    <span>Safe, family-friendly community</span>
                  </li>
                </ul>
              </div>

              {/* Utilities & Services */}
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-purple-800 mb-4">Utilities & Services</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Fiber internet available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Natural spring water supply</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Electricity connection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Refuse removal service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">✓</span>
                    <span>Low monthly levies</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Lifestyle & Investment Section */}
        <div className="container mx-auto px-4 py-16 bg-gradient-to-b from-green-50 to-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-800">Why Choose Hephzibah Estate?</h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="text-5xl mb-4">🌿</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Nature Living</h3>
                <p className="text-gray-600">Wake up to bird songs and mountain views every day</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="text-5xl mb-4">💰</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Great Value</h3>
                <p className="text-gray-600">Affordable luxury living in South Africa's most beautiful province</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="text-5xl mb-4">🏆</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Prime Location</h3>
                <p className="text-gray-600">Close to Loskop Dam, Bronkhorstspruit Dam, Panorama Route, and all amenities</p>
              </div>
            </div>

            {/* Investment Potential */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-lg shadow-xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-center">Investment Potential</h3>
              <p className="text-center text-green-100 mb-6">
                This property offers multiple income streams and strong capital appreciation potential
              </p>
              
              <ul className="space-y-4 max-w-2xl mx-auto">
                <li className="flex items-start gap-3">
                  <span className="text-green-300 text-xl flex-shrink-0">✓</span>
                  <div>
                    <p className="font-semibold">Airbnb Income</p>
                    <p className="text-sm text-green-100">High tourism area, strong rental demand year-round</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 text-xl flex-shrink-0">✓</span>
                  <div>
                    <p className="font-semibold">Retirement Haven</p>
                    <p className="text-sm text-green-100">Perfect for peaceful retirement in nature</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 text-xl flex-shrink-0">✓</span>
                  <div>
                    <p className="font-semibold">Holiday Home Investment</p>
                    <p className="text-sm text-green-100">Near tourist attractions, Airbnb potential</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 text-xl flex-shrink-0">✓</span>
                  <div>
                    <p className="font-semibold">Family Escape</p>
                    <p className="text-sm text-green-100">Safe estate, nature activities, excellent schools nearby</p>
                  </div>
                </li>
              </ul>
            </div>

            <p className="mt-6 text-center text-sm text-gray-500 italic">
              Note: Financial calculations are estimates. Consult with a financial advisor and bond originator for personalized advice.
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className="container mx-auto px-4 py-16 bg-gradient-to-b from-green-600 to-green-700">
          <h2 className="text-4xl font-bold text-center mb-4 text-white">Schedule Your Viewing Today!</h2>
          <p className="text-center text-lg mb-8 text-white max-w-2xl mx-auto">
            Don't miss this opportunity! Contact us now to arrange a viewing of this exceptional Hephzibah Estate property in Mpumalanga.
          </p>
          <div className="max-w-md mx-auto bg-white shadow-2xl rounded-lg p-8">
            <div className="space-y-4">
              {/* WhatsApp Chat Button */}
              <a
                href="https://wa.me/27826535566?text=Hi!%20I'm%20interested%20in%20the%203-bedroom%20home%20at%20Hephzibah%20Estate,%20Mpumalanga.%20Can%20I%20schedule%20a%20viewing?"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-full transition duration-300 text-center shadow-lg flex items-center justify-center gap-2"
              >
                <span className="text-2xl">💬</span>
                <span>Chat on WhatsApp</span>
              </a>
              
              {/* Phone Call Button */}
              <a
                href="tel:+27826535566"
                className="block w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-full transition duration-300 text-center shadow-lg flex items-center justify-center gap-2"
              >
                <span className="text-2xl">📞</span>
                <span>Call: 082 653 5566</span>
              </a>
              
              {/* Email Button */}
              <a
                href="mailto:relaxsolar@gmail.com?subject=Inquiry%20about%20Hephzibah%20Estate%20Property&body=Hi,%0D%0A%0D%0AI'm%20interested%20in%20the%203-bedroom%20home%20for%20sale%20at%20Hephzibah%20Estate,%20Mpumalanga.%20Please%20send%20me%20more%20information.%0D%0A%0D%0AThank%20you!"
                className="block w-full bg-red-500 hover:bg-red-600 text-white font-bold py-4 rounded-full transition duration-300 text-center shadow-lg flex items-center justify-center gap-2"
              >
                <span className="text-2xl">✉️</span>
                <span>Email Us</span>
              </a>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-gray-600 text-sm">
                <span className="font-semibold">Showings available 7 days a week</span><br />
                Response time: Within 2 hours during business hours
              </p>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 max-w-4xl mx-auto bg-white bg-opacity-10 rounded-lg p-6">
            <div className="grid md:grid-cols-3 gap-6 text-center text-white">
              <div>
                <TvIcon size={40} className="mx-auto mb-2" />
                <p className="font-bold">Featured on TV</p>
                <p className="text-sm opacity-90">Verified property</p>
              </div>
              <div>
                <Clock size={40} className="mx-auto mb-2" />
                <p className="font-bold">Quick Response</p>
                <p className="text-sm opacity-90">Within 2 hours</p>
              </div>
              <div>
                <Home size={40} className="mx-auto mb-2" />
                <p className="font-bold">Ready to Move In</p>
                <p className="text-sm opacity-90">Immediate occupation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - SEO Keywords */}
        <footer className="bg-gray-800 text-white py-8">
          <div className="container mx-auto px-4">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Hephzibah Estate, Mpumalanga</h3>
              <p className="text-gray-300">3-Bedroom Nature Home for Sale | R1,000,000</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-6 text-sm">
              <div>
                <h4 className="font-bold mb-3 text-green-400">Property Features</h4>
                <ul className="space-y-1 text-gray-300">
                  <li>3 Bedrooms, 2 Bathrooms</li>
                  <li>Solar Power System</li>
                  <li>2000m² Stand</li>
                  <li>Income-Generating Outbuildings</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-3 text-green-400">Location</h4>
                <ul className="space-y-1 text-gray-300">
                  <li>Mpumalanga Province, South Africa</li>
                  <li>60 min to Loskop Dam</li>
                  <li>On the Panorama Route</li>
                  <li>2.5 hours from Johannesburg</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-3 text-green-400">Keywords</h4>
                <p className="text-gray-300 text-xs leading-relaxed">
                  Hephzibah Estate, Mpumalanga property, 3 bedroom house for sale South Africa, retirement homes Mpumalanga, nature estate, homes near Loskop Dam, homes near Bronkhorstspruit Dam, Panorama Route property, remote work home, holiday home investment, affordable Mpumalanga living
                </p>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6 text-center text-gray-400 text-sm">
              <p>&copy; 2025 Daryl du Preez - Hephzibah Estate Property Listing. All rights reserved.</p>
              <p className="mt-2">Contact: 082 653 5566 | relaxsolar@gmail.com</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PropertyLandingPage;